import React from "react";
import { LuX } from "react-icons/lu";

/**
 * Groups busy slots by person email and renders a timeline strip per person.
 *
 * Props:
 *   busySlots      - Array<{email, name, start, end, subject, show_as}>
 *   selectedRange  - { start: string|Date, end: string|Date } | null
 *   startDate      - string YYYY-MM-DD (leftmost edge of strip)
 *   onRemoveAttendee - function(email: string)
 *   loading        - bool
 */
function AvailabilityStrip({
	busySlots = [],
	queriedPeople = [],
	selectedRange = null,
	startDate,
	onRemoveAttendee,
	loading,
}) {
	const selectedStart = selectedRange?.start ? new Date(selectedRange.start) : null;
	const selectedEnd = selectedRange?.end ? new Date(selectedRange.end) : null;
	const centerTime = selectedStart || new Date(`${startDate}T12:00:00`);
	const stripStart = new Date(centerTime.getTime() - 6 * 60 * 60 * 1000);
	const stripEnd = new Date(centerTime.getTime() + 6 * 60 * 60 * 1000);
	const totalMs = stripEnd - stripStart;

	function pct(date) {
		return Math.max(0, Math.min(100, ((new Date(date) - stripStart) / totalMs) * 100));
	}

	function fmt(isoStr) {
		if (!isoStr) return "";
		const d = new Date(isoStr);
		return d.toLocaleString([], {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	function fmtTime(isoStr) {
		if (!isoStr) return "";
		const d = new Date(isoStr);
		return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	}

	function fmtHour(date) {
		return new Date(date).toLocaleTimeString([], {
			hour: "numeric",
			hour12: true,
		});
	}

	function getVisibleSlot(slot) {
		const rawStart = new Date(slot.start);
		const rawEnd = new Date(slot.end);

		if (!(rawEnd > stripStart && rawStart < stripEnd)) return null;

		const start = rawStart < stripStart ? stripStart : rawStart;
		const end = rawEnd > stripEnd ? stripEnd : rawEnd;
		if (!(end > start)) return null;

		return {
			...slot,
			start,
			end,
		};
	}

	// Group busy slots by person
	const byPerson = {};
	for (const slot of busySlots) {
		if (!byPerson[slot.email]) {
			byPerson[slot.email] = { name: slot.name || slot.email, slots: [] };
		}
		byPerson[slot.email].slots.push(slot);
	}

	// Always show every queried person; fall back to whoever has busy slots
	const displayPeople = queriedPeople.length > 0
		? queriedPeople.map(p => ({
			email: p.email,
			name: p.name,
			slots: byPerson[p.email]?.slots || [],
		}))
		: Object.entries(byPerson).map(([email, data]) => ({
			email,
			name: data.name,
			slots: data.slots,
		}));

	const hours = Array.from({ length: 12 }, (_, hour) => {
		const time = new Date(stripStart);
		time.setHours(stripStart.getHours() + hour, 0, 0, 0);
		return time;
	});

	const showAs = {
		busy: "#ef4444",
		tentative: "#f59e0b",
		oof: "#8b5cf6",
		workingElsewhere: "#6b7280",
	};

	const hasVisibleBusy = displayPeople.some(person => person.slots.some(slot => getVisibleSlot(slot)));
	const hasSelectedWindowConflict = Boolean(selectedStart && selectedEnd) && displayPeople.some((person) =>
		person.slots.some((slot) => {
			const visibleSlot = getVisibleSlot(slot);
			if (!visibleSlot) return false;
			return selectedStart < visibleSlot.end && selectedEnd > visibleSlot.start;
		})
	);

	const subtitleText = selectedStart && selectedEnd
		? (hasSelectedWindowConflict ? "Busy during selected time" : "Selected time appears free")
		: (hasVisibleBusy ? "Busy blocks for selected teammates" : "All teammates are available");

	const subtitleColor = selectedStart && selectedEnd
		? (hasSelectedWindowConflict ? "var(--color-danger)" : "var(--color-highlight)")
		: "var(--color-fg-muted)";

	if (loading) {
		return (
			<div className="availability-strip">
				<div className="availability-loading">
					<span className="availability-spinner" />
					Checking calendars...
				</div>
			</div>
		);
	}

	// Nothing to show until a query has been made
	if (displayPeople.length === 0) return null;

	return (
		<div className="availability-strip">
			<div className="availability-strip-header">
				<div>
					<div className="availability-strip-subtitle" style={{ color: subtitleColor }}>
						{subtitleText}
					</div>
				</div>
				{selectedStart && selectedEnd ? (
					<div className="availability-selected-chip">
						{fmt(selectedStart.toISOString())} - {fmtTime(selectedEnd.toISOString())}
					</div>
				) : null}
			</div>

			<div className="avail-timeline-header avail-timeline-header--hours">
				<div className="avail-row-label avail-row-label--spacer" />
				<div className="avail-timeline-bar avail-timeline-bar--header avail-timeline-bar--hours">
					{hours.map((hour) => (
						<div
							key={hour.toISOString()}
							className="avail-hour-tick"
						>
							{fmtHour(hour)}
						</div>
					))}
				</div>
			</div>

			{/* Per-person rows */}
			{displayPeople.map(person => (
				<div key={person.email} className="avail-row">
					<div className="avail-row-label avail-row-label--with-action" title={person.email}>
						{onRemoveAttendee ? (
							<button
								type="button"
								className="avail-remove-attendee-button"
								onClick={() => onRemoveAttendee(person.email)}
								aria-label={`Remove ${person.name}`}
								title={`Remove ${person.name}`}
							>
								<LuX />
							</button>
						) : null}
						<span className="avail-row-label-text">{person.name}</span>

					</div>
					<div className="avail-timeline-bar">
						{selectedStart && selectedEnd && (
							<div
								className="avail-selected-block"
								style={{
									left: `${pct(selectedStart)}%`,
									width: `${Math.max(0.4, pct(selectedEnd) - pct(selectedStart))}%`,
								}}
								title={`Selected meeting window · ${fmt(selectedStart.toISOString())} - ${fmtTime(selectedEnd.toISOString())}`}
							/>
						)}
						{person.slots.map((slot, i) => {
							const visibleSlot = getVisibleSlot(slot);
							if (!visibleSlot) return null;
							const left = pct(visibleSlot.start);
							const width = Math.max(0.4, pct(visibleSlot.end) - left);
							const color = showAs[visibleSlot.show_as] || "#ef4444";
							return (
								<div
									key={i}
									className="avail-busy-block"
									style={{ left: `${left}%`, width: `${width}%`, background: color }}
									title={`${visibleSlot.subject || "Busy"} · ${fmt(visibleSlot.start)} – ${fmtTime(visibleSlot.end)}`}
								/>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
}

export default AvailabilityStrip;
